import React, { createContext, useContext, ReactNode } from 'react';
import * as signalR from '@microsoft/signalr';
import { useRealtimeConnection } from '@/hooks/useRealtimeConnection';
import { useMessagingNotifications, NewMessageEvent } from '@/hooks/useMessagingNotifications';
import {
    useVoipSmsNotifications,
    NewSmsEvent,
    SmsStatusUpdatedEvent,
} from '@/hooks/useVoipSmsNotifications';
import { useWebDialerRegistration } from '@/hooks/useWebDialerRegistration';

interface RealtimeContextValue {
    connection: signalR.HubConnection | null;
    status: 'disconnected' | 'connecting' | 'connected' | 'reconnecting';
    /** Subscribe to NewMessage events. Returns unsubscribe function. Presence suppresses toast notifications. */
    subscribeNewMessage: (handler: (event: NewMessageEvent) => void) => () => void;
    /** Subscribe to NewMessage events without suppressing toast notifications. */
    subscribeNewMessageBackground: (handler: (event: NewMessageEvent) => void) => () => void;
    /** Subscribe to NewSms events. Presence suppresses toast notifications. */
    subscribeNewSms: (handler: (event: NewSmsEvent) => void) => () => void;
    /** Subscribe to NewSms events without suppressing toast notifications. */
    subscribeNewSmsBackground: (handler: (event: NewSmsEvent) => void) => () => void;
    /** Subscribe to SmsStatusUpdated events. */
    subscribeSmsStatusUpdated: (handler: (event: SmsStatusUpdatedEvent) => void) => () => void;
    /** Consume the latest buffered SmsStatusUpdated event for a message. */
    consumeLatestSmsStatus: (messageId: string) => SmsStatusUpdatedEvent | undefined;
}

const RealtimeContext = createContext<RealtimeContextValue>({
    connection: null,
    status: 'disconnected',
    subscribeNewMessage: () => () => { },
    subscribeNewMessageBackground: () => () => { },
    subscribeNewSms: () => () => { },
    subscribeNewSmsBackground: () => () => { },
    subscribeSmsStatusUpdated: () => () => { },
    consumeLatestSmsStatus: () => undefined,
});

/**
 * Provider that establishes the SignalR connection once and shares it
 * with all child components via context.
 *
 * Also manages global event handlers (messaging notifications, etc.).
 */
export const RealtimeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const { connection, status } = useRealtimeConnection();
    const { subscribe: subscribeNewMessage, subscribeBackground: subscribeNewMessageBackground } = useMessagingNotifications(connection);
    const {
        subscribeNewSms,
        subscribeNewSmsBackground,
        subscribeSmsStatusUpdated,
        consumeLatestSmsStatus,
    } = useVoipSmsNotifications(connection);
    // Web dialer: register/unregister this tab for inbound calls based on the routing config.
    useWebDialerRegistration(connection, status);

    return (
        <RealtimeContext.Provider value={{
            connection,
            status,
            subscribeNewMessage,
            subscribeNewMessageBackground,
            subscribeNewSms,
            subscribeNewSmsBackground,
            subscribeSmsStatusUpdated,
            consumeLatestSmsStatus,
        }}>
            {children}
        </RealtimeContext.Provider>
    );
};

/**
 * Hook to access the shared realtime connection from any component.
 */
export const useRealtime = (): RealtimeContextValue => {
    return useContext(RealtimeContext);
};
